import React, { useState } from 'react';
import { Box, Typography, Stack, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Divider } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Define the custom color constants based on the design (Green and Grey)
const GREEN_COLOR = '#008060'; // Adjusted a standard green to match the Figma preview
const GREY_FONT_COLOR = '#828584'; // The 'Grey font' color from the sidebar

// Periods for revenue pages; "This month" is the default
export const TIME_PERIODS = ["Today", "This week", "This month", "Last month", "This year", "All time"];

// Chosen from the "Custom range" item; dates are YYYY-MM-DD and both days are included
export const CUSTOM_PERIOD = "Custom range";
export interface CustomDateRange {
  from: string;
  to: string;
}

const parseDay = (value: string) => {
  const [y, m, d] = value.split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};

const formatDay = (value: string, withYear: boolean) =>
  parseDay(value)?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}) }) || '';

// Text for a custom range, e.g. "12 Oct – 20 Oct 2026"
export const formatCustomRange = (range: CustomDateRange) =>
  range.from === range.to
    ? formatDay(range.from, true)
    : `${formatDay(range.from, range.from.slice(0, 4) !== range.to.slice(0, 4))} – ${formatDay(range.to, true)}`;

// [start, end) of a period in local time; null = no limit
export const getPeriodRange = (period: string, custom?: CustomDateRange | null): [Date | null, Date | null] => {
  if (period === CUSTOM_PERIOD && custom) {
    const start = parseDay(custom.from);
    const end = parseDay(custom.to);
    // The end day is included, so the range runs to the start of the next day
    if (end) end.setDate(end.getDate() + 1);
    return [start, end];
  }
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (period) {
    case "Today":
      return [startOfToday, null];
    case "This week": {
      // Week starts on Monday
      const start = new Date(startOfToday);
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
      return [start, null];
    }
    case "This month":
      return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    case "Last month":
      return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    case "This year":
      return [new Date(now.getFullYear(), 0, 1), null];
    default:
      return [null, null];
  }
};

interface TimePeriodSelectorProps {
  /** The currently selected time period text (e.g., "This month") */
  selectedPeriod: string;
  /** Callback function when the selector is clicked */
  onClick?: () => void;
  /** Periods to choose from; when given, clicking opens a menu */
  options?: string[];
  /** Called with the chosen period */
  onChange?: (period: string) => void;
  /** The applied custom range; shown as the label while the period is CUSTOM_PERIOD */
  customRange?: CustomDateRange | null;
  /** When given, the menu ends with "Custom range", which asks for From and To dates */
  onCustomRange?: (range: CustomDateRange) => void;
}

const todayValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

export const TimePeriodSelector: React.FC<TimePeriodSelectorProps> = ({
  selectedPeriod,
  onClick,
  options,
  onChange,
  customRange,
  onCustomRange,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [draft, setDraft] = useState<CustomDateRange>({ from: '', to: '' });
  const hasMenu = !!options?.length;
  const label = selectedPeriod === CUSTOM_PERIOD && customRange ? formatCustomRange(customRange) : selectedPeriod;
  const draftError = draft.from && draft.to && draft.from > draft.to ? '"From" must be on or before "To"' : '';

  const openRangeDialog = () => {
    setAnchor(null);
    setDraft(customRange || { from: '', to: todayValue() });
    setRangeOpen(true);
  };

  const applyRange = () => {
    if (!draft.from || !draft.to || draftError) return;
    setRangeOpen(false);
    onCustomRange?.(draft);
  };

  return (
    <>
    <Box
      role={hasMenu ? 'button' : undefined}
      tabIndex={hasMenu ? 0 : undefined}
      aria-haspopup={hasMenu ? 'menu' : undefined}
      aria-expanded={hasMenu ? !!anchor : undefined}
      aria-label={hasMenu ? `Time period: ${label}` : undefined}
      onKeyDown={hasMenu ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAnchor(e.currentTarget); } } : undefined}
      // The main container for the pill shape and border
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '8px 20px', // Adjust padding for vertical and horizontal spacing
        borderRadius: '50px', // High border radius for the pill shape
        border: `2px solid ${GREEN_COLOR}`,
        cursor: 'pointer',
        transition: 'background-color 0.3s',
        '&:hover': {
          backgroundColor: 'rgba(0, 128, 96, 0.05)', // Light green hover effect
        },
      }}
      onClick={(e) => {
        if (hasMenu) setAnchor(e.currentTarget);
        onClick?.();
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        {/* Calendar Icon */}
        <CalendarMonthIcon sx={{ fontSize: "1rem", color: GREEN_COLOR }} />

        {/* Text Label */}
        <Typography
          variant="h5" // Use a larger variant for prominence
          sx={{
            fontFamily: 'Lexend, sans-serif', // Using 'Lexend' if available, otherwise fallback
            fontWeight: 400,
            fontSize: '0.89rem', // Larger font size for the main text
            color: "#022219",
            lineHeight: 1, // Ensure text is centered vertically
          }}
        >
          {label}
        </Typography>

        {/* Dropdown Arrow */}
        <KeyboardArrowDownIcon sx={{ fontSize: "1rem", color: GREY_FONT_COLOR }} />
      </Stack>
    </Box>
    {hasMenu && (
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {options!.map((option) => (
          <MenuItem
            key={option}
            selected={option === selectedPeriod}
            onClick={() => {
              setAnchor(null);
              onChange?.(option);
            }}
          >
            {option}
          </MenuItem>
        ))}
        {onCustomRange && <Divider />}
        {onCustomRange && (
          <MenuItem selected={selectedPeriod === CUSTOM_PERIOD} onClick={openRangeDialog}>
            {CUSTOM_PERIOD}…
          </MenuItem>
        )}
      </Menu>
    )}
    {onCustomRange && (
      <Dialog open={rangeOpen} onClose={() => setRangeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Custom date range</DialogTitle>
        <DialogContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="From"
              type="date"
              size="small"
              fullWidth
              value={draft.from}
              onChange={(e) => setDraft((prev) => ({ ...prev, from: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: draft.to || undefined }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              fullWidth
              value={draft.to}
              onChange={(e) => setDraft((prev) => ({ ...prev, to: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: draft.from || undefined }}
              error={!!draftError}
              helperText={draftError || ' '}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRangeOpen(false)} sx={{ color: GREY_FONT_COLOR, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!draft.from || !draft.to || !!draftError}
            onClick={applyRange}
            sx={{ bgcolor: GREEN_COLOR, textTransform: 'none', '&:hover': { bgcolor: '#066e4f' } }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    )}
    </>
  );
};